/**
 * A patch minifier takes a value and a patch and returns the minimum patch that
 * would make the same changes as the given patch.
 *
 * `undefined` is used in two distinct ways, to represent that a value is
 * unknown **OR** to represent that a patch makes no change. This allows patch
 * minifiers to be used in forms that only edit part of a value.
 *
 * When the previous value is unknown (represented by `undefined`), patch
 * minifiers should assume the worst case and return a patch that would make the
 * same changes as the given patch on all possible values.
 *
 * @template P The type of the patch.
 * @template V The type of a value that can be patched. Patches and values can
 *             have incompatible types. For example the value might be an array,
 *             but the patch might be the additions and removals.
 */

export function minPatchPrimitive(prev, patch) {
  if (prev === undefined || prev !== patch) {
    return patch;
  }
}

/**
 * This is used for a field that is always required in a patch. The `id` of an
 * entity is an example.
 */
export function minPatchRequired(_prev, patch) {
  return patch;
}
function baseMinPatch(minPatch) {
  return (prev, patch) => {
    if (prev === undefined || patch === undefined) {
      return patch;
    }

    // Note, short cutting on `prev === patch` does not work with all minifiers.
    // Some have required properties, even when there is no change.
    if (prev === patch) {
      return undefined;
    }
    return minPatch(prev, patch);
  };
}
function isDefinedEntry(entry) {
  return entry[1] !== undefined;
}
function minPatchEntries(toEntries) {
  return (prev, patch) => {
    const entries = toEntries(prev, patch).filter(isDefinedEntry);
    if (entries.length === 0) {
      return undefined;
    }
    return Object.fromEntries(entries);
  };
}
export function minPatchObject(schema) {
  return minPatchEntries((prev, patch) => Object.entries(schema).map(([key, minPatchValue]) => [key, minPatchValue(prev?.[key], patch?.[key])]));
}
export function minPatchRecord(keys, minPatchValue) {
  return minPatchEntries((prev, patch) => Array.from(keys, key => [key, minPatchValue(prev?.[key], patch?.[key])]));
}
export function minPatchSetLikeArray(areElementsEqual) {
  return baseMinPatch((prev, patch) => haveSameElements(prev, patch, areElementsEqual) ? undefined : patch);
}

/**
 * Returns `true` if the two arrays have the same elements, regardless of order
 * and multiple entries.
 */
function haveSameElements(xs, ys, areElementsEqual) {
  return isSubSet(xs, ys, areElementsEqual) && isSubSet(ys, xs, areElementsEqual);
}
function isSubSet(xs, ys, areElementsEqual) {
  return xs.every(x => areElementsEqual ? ys.some(y => areElementsEqual(x, y)) : ys.includes(x));
}